// Browserteil der Klickzählung für direkte Hub-Links aus Newslettern.
//
// Ein Newsletter-Link auf eine Hub-Seite trägt ?nlk=<recipientId>.<signatur>
// (lib/newsletter-link.ts). Diese Datei meldet den Wert an
// /api/newsletter/track/intern und nimmt ihn aus der Adresse, damit Neuladen,
// Lesezeichen und Weitergeben nicht erneut zählen.
//
// Zwei Fälle:
//   1. Der Parameter steht in der Adresse der Seite (direkter Aufruf, App-Link).
//   2. Ohne Sitzung leitet die Middleware auf /login?next=<pfad+query> um. Dann
//      steckt der Parameter in `next`: Der Klick zählt schon hier, auch wenn
//      die Person sich nie anmeldet, und `next` verliert den Parameter, damit
//      die Zielseite nach dem Login nicht ein zweites Mal zählt.
//
// Kein Import von Serverbausteinen (crypto). Prüfen tut ausschließlich die Route.

const PARAM = 'nlk'
// Gleiches Format wie KLICK_WERT_MUSTER in lib/newsletter-link.ts
const WERT_MUSTER = /^[A-Za-z0-9_-]{1,64}\.[A-Za-z0-9_-]{22}$/

const gemeldet = new Set<string>()
let laufend: Promise<void> = Promise.resolve()

/** Wert des Parameters aus einer Query (`?a=1&nlk=…`), sonst null. */
function wertAus(search: string): string | null {
  return new URLSearchParams(search).get(PARAM)
}

/**
 * Query ohne den Parameter, übrige Teile in Originalschreibweise. Bewusst
 * kein URLSearchParams.toString(): das schriebe z. B. %20 als + um, und die
 * Zielseite bekäme eine andere Adresse als die aus der Mail.
 */
function ohneParam(search: string): string {
  if (!search || search === '?') return ''
  const teile = search.slice(1).split('&').filter(t => t !== '' && t.split('=')[0] !== PARAM)
  return teile.length > 0 ? `?${teile.join('&')}` : ''
}

const SPEICHER_PRAEFIX = 'nlk-gemeldet:'

/** Schon in diesem Tab gemeldet? Auch über Neuladen hinweg (sessionStorage). */
function schonGemeldet(nlk: string): boolean {
  if (gemeldet.has(nlk)) return true
  try {
    return window.sessionStorage.getItem(SPEICHER_PRAEFIX + nlk) === '1'
  } catch {
    return false
  }
}

function senden(nlk: string, pfad: string): void {
  if (!WERT_MUSTER.test(nlk) || schonGemeldet(nlk)) return
  gemeldet.add(nlk)
  try {
    window.sessionStorage.setItem(SPEICHER_PRAEFIX + nlk, '1')
  } catch {
    // Privater Modus o. ä.: dann schützt nur der Speicher im Modul.
  }
  laufend = fetch('/api/newsletter/track/intern', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ nlk, pfad }),
    credentials: 'same-origin',
    // Die Meldung soll auch ankommen, wenn die Seite gleich weiterspringt.
    keepalive: true,
  }).then(() => undefined, () => undefined)
}

/** `next` der Login-Seite zerlegt, sofern es den Parameter trägt. */
function loginNext(search: string): { nlk: string; nextNeu: string } | null {
  const next = new URLSearchParams(search).get('next')
  if (!next || !next.startsWith('/') || next.startsWith('//') || next.startsWith('/\\')) return null
  const q = next.indexOf('?')
  if (q < 0) return null
  const nlk = wertAus(next.slice(q))
  if (nlk === null) return null
  return { nlk, nextNeu: `${next.slice(0, q)}${ohneParam(next.slice(q))}` }
}

// ── Adresse bereinigen ──────────────────────────────────────────────────────
//
// Abhängigkeit von Next 16.2.4 (node_modules/next/dist/client/components/
// app-router.js): Der App-Router ersetzt window.history.replaceState in einem
// useEffect durch eine eigene Fassung (Instanz-Eigenschaft auf
// window.history). Nur die überträgt Nexts internen History-Zustand (__NA,
// Baum) und meldet die neue Adresse an den Router. Ein Aufruf VOR diesem
// Patch löscht den Zustand (Zurück-Taste kaputt) und der Router behält die
// alte Adresse mit nlk (kommt bei router.refresh zurück). Beim ersten Laden
// läuft unser Layout-Effekt aber genau davor. Deshalb: melden sofort,
// bereinigen erst, wenn der Patch steht (Abfrage alle 50 ms, höchstens 2 s).
//
// Kein Ersatzweg über router.replace: Das löst einen Server-Abruf aus und
// rendert die Seite neu, mitten in deren eigenen Ladeeffekten (Ein-Klick-
// Zusage). Steht der Patch nach 2 s nicht, bleibt nlk in der Adresse; ein
// Neuladen zählt dank sessionStorage trotzdem nicht doppelt. Ändert eine
// spätere Next-Version den Patch (z. B. Navigation API), greift genau dieser
// Fall, die Zählung bleibt richtig.

const BEREINIGEN_TAKT_MS = 50
const BEREINIGEN_MAX_MS = 2000

function nextHatHistoryPatch(): boolean {
  return Object.prototype.hasOwnProperty.call(window.history, 'replaceState')
}

/** Liest die AKTUELLE Adresse neu (eine Seite kann die Query inzwischen selbst geleert haben). */
function bereinigen(): void {
  const { pathname, search, hash } = window.location
  let ziel: string | null = null
  if (wertAus(search) !== null) {
    ziel = `${pathname}${ohneParam(search)}${hash}`
  } else if (pathname === '/login') {
    const l = loginNext(search)
    if (l) {
      const sp = new URLSearchParams(search)
      sp.set('next', l.nextNeu)
      ziel = `${pathname}?${sp.toString()}${hash}`
    }
  }
  if (ziel === null) return
  try {
    window.history.replaceState(null, '', ziel)
  } catch {
    // Adresse bleibt stehen; gezählt ist trotzdem.
  }
}

function bereinigenWennBereit(): void {
  const start = Date.now()
  const versuch = () => {
    if (nextHatHistoryPatch()) {
      bereinigen()
      return
    }
    if (Date.now() - start < BEREINIGEN_MAX_MS) setTimeout(versuch, BEREINIGEN_TAKT_MS)
  }
  // Nie synchron im Layout-Effekt: der Patch meldet die Adresse per
  // startTransition an den Router, das gehört nicht in den Commit.
  setTimeout(versuch, 0)
}

/** Prüft die aktuelle Adresse auf den Parameter, meldet ihn und bereinigt die Adresse danach. */
export function meldeNewsletterKlick(): void {
  if (typeof window === 'undefined') return
  const { pathname, search } = window.location

  const direkt = wertAus(search)
  if (direkt !== null) {
    senden(direkt, `${pathname}${ohneParam(search)}`)
    bereinigenWennBereit()
    return
  }

  if (pathname !== '/login') return
  const l = loginNext(search)
  if (!l) return
  senden(l.nlk, l.nextNeu)
  bereinigenWennBereit()
}

/**
 * Wartet, bis eine laufende Meldung durch ist (höchstens `maxMs`). Für
 * Seiten, die direkt beim Laden etwas auslösen (Ein-Klick-Zusage aus der
 * TALKOVER-Mail): Die Statistik wertet eine Anmeldung nur dann als „über
 * den Link“, wenn sie nach dem Klick liegt.
 */
export function newsletterKlickAbgeschlossen(maxMs = 1500): Promise<void> {
  return Promise.race([laufend, new Promise<void>(r => setTimeout(r, maxMs))])
}
